/**
 * Trip Tool
 * Floating Where/When journey pill: destination picker (country -> resort),
 * date range calendar (hard 2-14 day rule), trip state persistence, and the
 * search action into the all-products collection.
 *
 * Storage: primary key `cs-trip` (localStorage, pending Danai sign-off - switch
 * STORAGE below to sessionStorage to revert). Every write mirrors the legacy
 * keys (`dates`, `scaf-dates` in sessionStorage) so existing PDP, price-updater
 * and Supercycle code keeps working untouched. Buyer context: `cs-context-iso`
 * records the intended country, `cs-context-pending` a market switch waiting
 * for the next navigation (see PendingMarket).
 */

(function () {
  'use strict';

  if (customElements.get('trip-tool')) return;

  var STORAGE = window.localStorage;
  var LEGACY = window.sessionStorage;
  var KEY = 'cs-trip';
  var MIN_DAYS = 2;
  var MAX_DAYS = 14;
  // Lead-time buffer floor: a start inside the next LEAD_DAYS days stays
  // selectable but counts as short notice - the calendar swaps its rule
  // notice for the last-minute warning and the PDP (trip-pdp.js, same
  // constant) keeps add to cart disabled. leadCutoffIso() widens this
  // whenever the theme's configured lead time reaches further out.
  var LEAD_DAYS = 2;
  // Earliest selectable start: tomorrow
  var MIN_START_OFFSET = 1;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  /**
   * Date helpers
   * Dates handled as `YYYY-MM-DD` strings; day maths in UTC to dodge DST.
   */
  function iso(date) {
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  }

  function fromIso(str) {
    var p = str.split('-');
    return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }

  function daysBetween(startIso, endIso) {
    var ms = Date.UTC.apply(null, endIso.split('-').map(Number).map(function (n, i) { return i === 1 ? n - 1 : n; })) -
      Date.UTC.apply(null, startIso.split('-').map(Number).map(function (n, i) { return i === 1 ? n - 1 : n; }));
    return Math.round(ms / 86400000);
  }

  function todayIso() {
    return iso(new Date());
  }

  function offsetIso(baseIso, days) {
    var p = baseIso.split('-').map(Number);
    return iso(new Date(p[0], p[1] - 1, p[2] + days));
  }

  /**
   * Earliest start the store treats as normal notice.
   *
   * The floor is LEAD_DAYS, but the theme's configured lead time wins when
   * it reaches further out: settings.extra_lead_time plus the cutoff and
   * delivery-day mapping, all of which date-utils.js folds into
   * calculateMinDate(). Dates before this stay selectable - they get the
   * last-minute warning and trip-pdp.js keeps add to cart off - so raising
   * the setting widens the warning rather than shrinking the calendar.
   *
   * calculateMinDate() returns local midnight on the target day, so it is
   * formatted with iso() like every other date here, not toISOString().
   */
  function leadCutoffIso() {
    var cutoff = offsetIso(todayIso(), LEAD_DAYS);
    var utils = window.dateUtils;
    if (utils && typeof utils.calculateMinDate === 'function') {
      try {
        var configured = iso(utils.calculateMinDate());
        if (configured > cutoff) return configured;
      } catch (e) {
        /* date-utils absent or throwing: fall back to the LEAD_DAYS floor */
      }
    }
    return cutoff;
  }

  function isShortNotice(startIso) {
    return Boolean(startIso) && startIso < leadCutoffIso();
  }

  function formatShort(isoStr) {
    var d = fromIso(isoStr);
    return d.getDate() + ' ' + MONTHS_SHORT[d.getMonth()];
  }

  function toLegacyDmy(isoStr) {
    return isoStr.split('-').reverse().join('/');
  }

  /**
   * Trip state store
   */
  var TripState = {
    read: function () {
      var state = null;
      try {
        state = JSON.parse(STORAGE.getItem(KEY));
      } catch (e) {
        state = null;
      }

      if (!state) state = this.hydrateFromLegacy();
      if (!state) return { country: null, resort: null, start: null, end: null, undecided: false };

      // Expire trips whose dates are already in the past
      if (state.end && state.end < todayIso()) {
        state.start = null;
        state.end = null;
      }
      // Normalise every key: a stored trip missing `undecided` would feed
      // undefined into classList.toggle, which then FLIPS instead of
      // removing - the calendar greys out at random
      return {
        country: state.country || null,
        resort: state.resort || null,
        start: state.start || null,
        end: state.end || null,
        undecided: Boolean(state.undecided)
      };
    },

    hydrateFromLegacy: function () {
      var legacy = null;
      try {
        legacy = JSON.parse(LEGACY.getItem('dates'));
      } catch (e) {
        legacy = null;
      }
      if (!legacy || !legacy.startDate) return null;
      return {
        country: null,
        resort: null,
        start: legacy.startDate.split('/').reverse().join('-'),
        end: legacy.endDate.split('/').reverse().join('-'),
        undecided: false
      };
    },

    write: function (state) {
      try {
        STORAGE.setItem(KEY, JSON.stringify(state));
      } catch (e) {
        /* storage unavailable - state stays in memory */
      }
      this.mirrorLegacy(state);
      document.dispatchEvent(new CustomEvent('trip:change', { detail: state }));
    },

    /**
     * Legacy bridge: keeps the exact shapes the existing theme code reads.
     */
    mirrorLegacy: function (state) {
      try {
        if (state.start && state.end) {
          var days = daysBetween(state.start, state.end) + 1;
          LEGACY.setItem('dates', JSON.stringify({
            startDate: toLegacyDmy(state.start),
            endDate: toLegacyDmy(state.end),
            days: days,
            index: Math.min(Math.max(days - 3, 0), 11)
          }));
          var scaf = (window.ScafDates && window.ScafDates.set(state.start, state.end)) ||
            { start_date: state.start, end_date: state.end };
          LEGACY.setItem('scaf-dates', JSON.stringify(scaf));
        } else {
          LEGACY.removeItem('dates');
          LEGACY.removeItem('scaf-dates');
        }
      } catch (e) {
        /* ignore */
      }
    }
  };

  window.CirkelTrip = TripState;

  /**
   * Deferred market switch (CIRKEL-273). Collections switch the market as
   * soon as the trip flow ends (marketRefresh) and the PDP runs its own
   * market flow; every other page keeps its render, because reloading the
   * page the shopper is reading is jarring. There the switch is saved
   * locally and rides along with the next navigation instead: the link
   * followed carries ?country (decorateLink), and a page reached any other
   * way - back button, typed URL, a script's redirect - applies it on
   * landing (enforceContext). The intended country moves with it, so the
   * page that lands agrees with `cs-context-iso`.
   */
  var PendingMarket = {
    read: function () {
      try {
        return window.localStorage.getItem('cs-context-pending') || null;
      } catch (error) {
        return null;
      }
    },

    queue: function (iso) {
      try {
        window.localStorage.setItem('cs-context-pending', iso.toUpperCase());
        window.localStorage.setItem('cs-context-iso', iso.toUpperCase());
      } catch (error) {
        /* private mode: the search action still switches */
      }
    },

    /** Drops a waiting switch; `iso`, when given, becomes the intended country. */
    clear: function (iso) {
      try {
        window.localStorage.removeItem('cs-context-pending');
        if (iso) window.localStorage.setItem('cs-context-iso', iso.toUpperCase());
      } catch (error) {
        /* private mode */
      }
    }
  };

  /**
   * <trip-tool> custom element
   */
  var TripTool = function () {
    return Reflect.construct(HTMLElement, [], TripTool);
  };
  TripTool.prototype = Object.create(HTMLElement.prototype);
  TripTool.prototype.constructor = TripTool;
  Object.setPrototypeOf(TripTool, HTMLElement);

  TripTool.prototype.connectedCallback = function () {
    var arrivedWithCountry = this.scrubCountryParam();
    this.enforceContext(arrivedWithCountry);
    var self = this;
    this.__pageshowEnforce = function (event) { if (event.persisted) self.enforceContext(false); };
    window.addEventListener('pageshow', this.__pageshowEnforce);
    this.state = TripState.read();
    // Re-mirror on every page load: sessionStorage dies with the tab while
    // the trip survives in localStorage, and Supercycle reads the legacy keys
    TripState.mirrorLegacy(this.state);
    this.destinations = this.readDestinations();
    this.viewMonth = this.initialMonth();
    this.pendingStart = null;

    this.els = {
      scrim: this.querySelector('.trip-tool__scrim'),
      whereLabel: this.querySelector('[data-where-label]'),
      whenLabel: this.querySelector('[data-when-label]'),
      compactPrimary: this.querySelector('[data-compact-primary]'),
      compactSecondary: this.querySelector('[data-compact-secondary]'),
      countryList: this.querySelector('[data-country-list]'),
      resortList: this.querySelector('[data-resort-list]'),
      resortsLabel: this.querySelector('[data-resorts-label]'),
      resortSearch: this.querySelector('[data-resort-search]'),
      months: this.querySelector('[data-months]'),
      whenPanel: this.querySelector('.trip-tool__panel--when'),
      wherePanel: this.querySelector('.trip-tool__panel--where'),
      sheetBarWhere: this.querySelector('[data-sheet-bar-where]'),
      sheetBarWhen: this.querySelector('[data-sheet-bar-when]'),
      sheetBarWhereValue: this.querySelector('[data-sheet-bar-where-value]'),
      sheetBarWhenValue: this.querySelector('[data-sheet-bar-when-value]')
    };

    this.onKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);

    // Re-render the calendar when crossing the mobile breakpoint
    this.mql = window.matchMedia('(max-width: 749px)');
    this.onBreakpoint = this.renderCalendar.bind(this);
    if (this.mql.addEventListener) this.mql.addEventListener('change', this.onBreakpoint);

    this.bind();
    this.renderCountries();
    this.renderCalendar();
    this.syncUi();

    // Dated cart items freeze the trip everywhere, not just on the PDP
    this.checkCartLock();
    this.onCartUpdate = this.checkCartLock.bind(this);
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    installCartHooks();

    // Town lists: warm the caches once the page is quiet so a country pick
    // never waits on the network (see fetchTowns / warmTowns)
    forgetLegacyTownsCache();
    var self3 = this;
    var whenIdle = window.requestIdleCallback || function (fn) { return window.setTimeout(fn, 1500); };
    whenIdle(function () { self3.warmTowns('idle'); }, { timeout: 5000 });

    // Another tab (or surface) changing the trip re-syncs this one
    var self2 = this;
    this.onStorage = function (event) {
      if (event.key && event.key !== 'cs-trip') return;
      self2.state = TripState.read();
      self2.renderCountries();
      self2.renderCalendar();
      self2.syncUi();
    };
    window.addEventListener('storage', this.onStorage);
  };

  TripTool.prototype.disconnectedCallback = function () {
    if (this.__pageshowEnforce) window.removeEventListener('pageshow', this.__pageshowEnforce);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    window.removeEventListener('storage', this.onStorage);
    if (this.mql && this.mql.removeEventListener) this.mql.removeEventListener('change', this.onBreakpoint);
    document.body.classList.remove('trip-tool-lock');
  };

  /**
   * Cart lock: any line carrying rental date properties freezes the trip -
   * dates apply cart-wide, so the pill must not change them mid-cart.
   */
  TripTool.prototype.checkCartLock = function () {
    var self = this;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cartLocked = (cart.items || []).some(function (item) {
          return item.properties && item.properties['Start date'] && item.properties['End date'];
        });
        self.classList.toggle('trip-tool--cart-locked', self.cartLocked);
      })
      .catch(function () { /* cart unreachable - leave unlocked */ });
  };

  /**
   * Every cart mutation goes through fetch or XHR to /cart/add|change|update|
   * clear - re-check the lock after each, whoever made the call. Installed
   * once per page regardless of how many trip-tool elements exist.
   */
  function installCartHooks() {
    if (window.__tripToolCartHooks) return;
    window.__tripToolCartHooks = true;
    var recheck = function () {
      window.setTimeout(function () {
        document.querySelectorAll('trip-tool').forEach(function (tool) {
          if (tool.checkCartLock) tool.checkCartLock();
        });
      }, 200);
    };
    var isCartUrl = function (url) {
      return /\/cart\/(add|change|update|clear)/.test(String(url || ''));
    };
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var promise = origFetch.apply(this, arguments);
      var url = (input && input.url) || input;
      if (isCartUrl(url)) promise.then(recheck, function () {});
      return promise;
    };
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartUrl(url)) this.addEventListener('loadend', recheck);
      return origOpen.apply(this, arguments);
    };
  }

  TripTool.prototype.cloneTemplate = function (name) {
    var tpl = this.querySelector('template[data-tpl-' + name + ']');
    return tpl ? tpl.content.cloneNode(true) : document.createDocumentFragment();
  };

  TripTool.prototype.readDestinations = function () {
    var node = this.querySelector('[data-trip-destinations]');
    var parsed = { countries: [] };
    try {
      parsed = JSON.parse(node.textContent);
    } catch (e) {
      /* leave empty */
    }
    parsed.countries.sort(function (a, b) {
      return (a.sort || 999) - (b.sort || 999) || a.name.localeCompare(b.name);
    });
    parsed.countries.forEach(function (country) {
      country.resorts = (country.resorts || []).map(function (resort) {
        return {
          name: resort.name,
          region: resort.region || resort.town || '',
          sort: resort.sort || 999,
          ski: resort.ski == null ? 1 : Number(resort.ski),
          featured: 1
        };
      });
      country.resorts.sort(function (a, b) {
        return (a.sort || 999) - (b.sort || 999) || a.name.localeCompare(b.name);
      });
      // CDN urls of the town feeds (see fetchTowns); empty strings mean "use the proxy"
      var feeds = country.feeds || {};
      country.feeds = { ski: feeds.ski || '', all: feeds.all || '' };
    });
    return parsed;
  };

  /**
   * Towns feed. The metaobjects carry only each country's featured resorts
   * (Liquid loops cap at 50); every other town comes as JSON in two tiers:
   *   ski - starred towns + ski resorts (a few hundred rows): the idle view
   *   all - every visible town (France: ~15k rows): what a search runs over
   * Each tier is a file the API publishes to Shopify Files, fetched from
   * the CDN (~50 ms) via the url in the country's `feeds`; without a url,
   * or when the CDN fetch fails, the same rows come through the app proxy
   * (0.7-2 s, never cached by Shopify). Rows: n name, r region, h handle,
   * s featured, k ski resort, l lift score.
   *
   * Caching: rows live in memory for the page and in localStorage across
   * visits. A CDN feed url embeds the content version, so a cache stamped
   * with the same url is current without any request; proxy rows are
   * trusted for TOWNS_MAX_AGE and revalidated with the ETag (304) after
   * that, the stale rows answering meanwhile. warmTowns prefetches, so a
   * country pick normally finds both tiers already here.
   */
  var TOWNS_URL = '/apps/theme/destinations/';
  var TOWNS_KEY = 'cs-towns-';
  var TOWNS_MAX_AGE = 6 * 60 * 60 * 1000;
  var TOWNS_TIER_RANK = { ski: 1, all: 2 };
  var townsMemory = {};
  var townsInflight = {};

  // Letters NFD leaves alone, folded so "\u00d8rsta" and "Orsta" agree
  var FOLD_LETTERS = { '\u00f8': 'o', '\u00e6': 'ae', '\u0153': 'oe', '\u00df': 'ss', '\u0142': 'l', '\u0111': 'd', '\u00f0': 'd', '\u00fe': 'th' };
  // Abbreviation families: any member matches any other ("St Moritz",
  // "St. Moritz", "Sankt Moritz", "Saint Moritz" are one key)
  var TOKEN_FAMILIES = { st: 'st', saint: 'st', sankt: 'st', san: 'st', ste: 'st', sainte: 'st', mt: 'mt', mont: 'mt', mount: 'mt' };

  /**
   * Search key: lowercase, accents stripped, punctuation folded to single
   * spaces, abbreviations canonical - "St. Moritz" and "st moritz" agree,
   * "Saas-Fee" and "saas fee" agree. The feed applies the same fold when it
   * picks which alternate names are worth shipping.
   */
  function normaliseText(value) {
    var folded = String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u00f8\u00e6\u0153\u00df\u0142\u0111\u00f0\u00fe]/g, function (ch) { return FOLD_LETTERS[ch] || ch; })
      .replace(/[^a-z0-9]+/g, ' ').trim();
    if (!folded) return '';
    return folded.split(' ').map(function (token) { return TOKEN_FAMILIES[token] || token; }).join(' ');
  }

  // Key with the spaces removed too, so "saasfee" still finds Saas-Fee
  function squashText(key) {
    return key.replace(/ /g, '');
  }

  /** A picker row with its search keys precomputed (name, aliases, region). */
  function townRow(name, region, handle, featured, ski, score, aliases) {
    var key = normaliseText(name);
    var aliasKeys = (aliases || []).map(normaliseText).filter(Boolean);
    return {
      name: name,
      region: region || '',
      handle: handle,
      featured: featured ? 1 : 0,
      ski: ski ? 1 : 0,
      score: score || 0,
      key: key,
      words: key ? key.split(' ') : [],
      squashed: squashText(key),
      aliases: aliasKeys,
      aliasWords: aliasKeys.map(function (alias) { return alias.split(' '); }),
      aliasesSquashed: aliasKeys.map(squashText),
      regionKey: normaliseText(region),
      regionWords: normaliseText(region) ? normaliseText(region).split(' ') : []
    };
  }

  function townsStore() {
    try { return window.localStorage; } catch (error) { return null; }
  }

  function readTownsCache(key) {
    var store = townsStore();
    if (!store) return null;
    try {
      var entry = JSON.parse(store.getItem(key) || 'null');
      return entry && entry.towns && entry.towns.length ? entry : null;
    } catch (error) { return null; }
  }

  function writeTownsCache(key, entry) {
    var store = townsStore();
    if (!store) return;
    var json;
    try { json = JSON.stringify(entry); } catch (error) { return; }
    try {
      store.setItem(key, json);
    } catch (error) {
      // Quota: the town caches are the only bulky tenant - drop them all, retry once
      try {
        var doomed = [];
        for (var i = 0; i < store.length; i++) {
          var name = store.key(i);
          if (name && name.indexOf(TOWNS_KEY) === 0) doomed.push(name);
        }
        doomed.forEach(function (name) { store.removeItem(name); });
        store.setItem(key, json);
      } catch (retryError) { /* memory still has the rows */ }
    }
  }

  // Earlier builds kept expanded rows in sessionStorage under the same prefix
  function forgetLegacyTownsCache() {
    try {
      var session = window.sessionStorage;
      var doomed = [];
      for (var i = 0; i < session.length; i++) {
        var name = session.key(i);
        if (name && name.indexOf(TOWNS_KEY) === 0) doomed.push(name);
      }
      doomed.forEach(function (name) { session.removeItem(name); });
    } catch (error) { /* private mode */ }
  }

  /** Flag rows whose name another row in the same list shares, so the label can add the region. */
  function markAmbiguousNames(rows) {
    var counts = {};
    var i;
    for (i = 0; i < rows.length; i++) counts[rows[i].key] = (counts[rows[i].key] || 0) + 1;
    for (i = 0; i < rows.length; i++) rows[i].ambiguous = counts[rows[i].key] > 1;
  }

  function expandTownRows(compact) {
    return compact.map(function (row) {
      return townRow(row.n, row.r || '', row.h, row.s, row.k, row.l, row.a);
    });
  }

  /** Where a tier comes from: the CDN feed when the country has one, else the proxy. */
  function townsSource(country, tier, forceProxy) {
    var url = !forceProxy && country.feeds ? country.feeds[tier] : '';
    if (url) return { url: url, version: url, cdn: true };
    return {
      url: TOWNS_URL + encodeURIComponent(country.iso) + '/towns' + (tier === 'ski' ? '?tier=ski' : ''),
      version: '',
      cdn: false
    };
  }

  /**
   * Rows of one tier for a country - resolves with the expanded rows, or
   * null when nothing (fresh or stale) is available. Only a successful,
   * non-empty answer is remembered: a disabled country (404) or a hiccup is
   * asked again on the next pick rather than sticking as "no towns".
   */
  TripTool.prototype.fetchTowns = function (country, tier) {
    tier = tier || 'all';
    var key = country.iso + '-' + tier;
    if (townsMemory[key]) return Promise.resolve(townsMemory[key]);
    if (townsInflight[key]) return townsInflight[key];

    var source = townsSource(country, tier, false);
    var cached = readTownsCache(TOWNS_KEY + key);
    if (cached) {
      townsMemory[key] = expandTownRows(cached.towns);
      var fresh = source.cdn ? cached.v === source.version : (Date.now() - (cached.at || 0)) < TOWNS_MAX_AGE;
      if (fresh) return Promise.resolve(townsMemory[key]);
    }

    var load = function (src) {
      var init = src.cdn ? { mode: 'cors', credentials: 'omit' } : { credentials: 'same-origin', headers: {} };
      if (cached && !src.cdn && cached.etag) init.headers['If-None-Match'] = cached.etag;
      return window.fetch(src.url, init)
        .then(function (response) {
          if (response.status === 304 && cached) {
            cached.at = Date.now();
            writeTownsCache(TOWNS_KEY + key, cached);
            return cached.towns;
          }
          if (!response.ok) return null;
          return response.json().then(function (data) {
            if (!data || !data.towns || !data.towns.length) return null;
            writeTownsCache(TOWNS_KEY + key, { v: src.version, etag: response.headers.get('ETag') || '', at: Date.now(), towns: data.towns });
            return data.towns;
          });
        })
        .catch(function () { return null; });
    };

    var request = load(source)
      .then(function (compact) {
        // A missing or broken feed file must not strand the picker
        return compact || !source.cdn ? compact : load(townsSource(country, tier, true));
      })
      .then(function (compact) {
        delete townsInflight[key];
        if (compact) townsMemory[key] = expandTownRows(compact);
        return townsMemory[key] || null;
      });
    townsInflight[key] = request;
    // Stale rows answer now; the request above refreshes them for later
    return cached ? Promise.resolve(townsMemory[key]) : request;
  };

  /**
   * Prefetch. 'idle' (after load): the ski tier of every country plus the
   * full list of the likely one (the saved trip's country, else the market's),
   * skipped on Save-Data and in seed mode (no feeds, proxy 404s). 'open'
   * (Where module opened): the full list of every country, so the pick is
   * instant whichever it is.
   */
  TripTool.prototype.warmTowns = function (level) {
    var self = this;
    var connection = navigator.connection;
    if (level === 'idle' && ((connection && connection.saveData) || this.destinations.source !== 'metaobject')) return;
    var likely = this.state.country ? this.state.country.iso : this.contextIso();
    this.destinations.countries.forEach(function (country) {
      self.fetchTowns(country, 'ski');
      if (level === 'open' || country.iso === likely) self.fetchTowns(country, 'all');
    });
  };

  TripTool.prototype.initialMonth = function () {
    var base = this.state.start ? fromIso(this.state.start) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  };

  /**
   * Event wiring
   */
  TripTool.prototype.bind = function () {
    var self = this;

    this.on('[data-open-where]', 'click', function () { self.open('where'); });
    this.on('[data-open-when]', 'click', function () { self.open('when'); });
    this.on('[data-open-compact]', 'click', function () { self.open('where'); });
    this.on('[data-submit]', 'click', function () { self.search(); });
    this.on('.trip-tool__scrim', 'click', function () { self.close(); });
    this.on('[data-change-country]', 'click', function () { self.setWhereView('countries'); });
    this.on('[data-market-slide-back]', 'click', function () {
      self.__marketPick = null;
      if (self.__preWhereState) self.restoreState(self.__preWhereState);
      self.setWhereView('countries');
    });
    this.on('[data-market-slide-continue]', 'click', function () {
      if (!self.__marketPick) return;
      document.dispatchEvent(new CustomEvent('trip:market-continue', { detail: self.__marketPick }));
    });
    this.on('[data-clear]', 'click', function () { self.clearDates(); });
    this.on('[data-toggle-dates]', 'click', function () { self.setUndecided(false); });
    this.on('[data-toggle-unsure]', 'click', function () {
      // "Not sure yet" answers the when step, so the sheet dismisses on the
      // same beat as a completed date pick instead of waiting for a scrim tap
      self.setUndecided(true);
      window.setTimeout(function () { self.close(); }, 350);
    });
    this.on('[data-month-prev]', 'click', function () { self.shiftMonth(-1); });
    this.on('[data-filters-toggle]', 'click', function () {
      if (self.getAttribute('data-open') === 'filters') {
        self.close();
      } else {
        self.open('filters');
      }
    });
    this.on('[data-month-next]', 'click', function () { self.shiftMonth(1); });

    if (this.els.sheetBarWhere) {
      this.els.sheetBarWhere.addEventListener('click', function () { self.open('where'); });
    }
    if (this.els.sheetBarWhen) {
      this.els.sheetBarWhen.addEventListener('click', function () { self.open('when'); });
    }
    if (this.els.resortSearch) {
      this.els.resortSearch.addEventListener('input', function () {
        self.filterResorts(this.value);
      });
    }
  };

  TripTool.prototype.on = function (selector, event, handler) {
    var el = this.querySelector(selector);
    if (el) el.addEventListener(event, handler);
  };

  TripTool.prototype.handleKeydown = function (event) {
    var open = this.getAttribute('data-open');
    if (!open) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    // Focus trap: Tab cycles within the open module
    if (event.key === 'Tab') {
      var panel = this.querySelector('.trip-tool__panel--' + open) || this;
      var focusables = panel.querySelectorAll('button:not([disabled]), input, a[href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /**
   * Open / close modules
   */
  TripTool.prototype.open = function (module) {
    // Escaping the verdict slide into another module counts as leaving
    // the verdict - same decision as Continue browsing
    if (module !== 'where' && this.resolveMarketVerdict()) return;
    // Filters stay usable with a locked cart; the trip modules do not
    if (this.cartLocked && (module === 'where' || module === 'when')) {
      this.showSearchError('locked');
      return;
    }
    this.clearSearchError();
    this.returnFocus = document.activeElement;
    this.setAttribute('data-open', module);
    document.body.classList.add('trip-tool-lock');
    if (module === 'where') {
      // Every country's full list, so whichever is picked answers at once
      this.warmTowns('open');
      // Inline (PDP): remember the trip as it stood so a mis-picked country
      // in the market flow can be rolled back cleanly
      if (this.classList.contains('trip-tool--inline')) {
        this.__preWhereState = JSON.parse(JSON.stringify(this.state));
      }
      // With a destination already chosen, reopen on its resort list with
      // the pick highlighted; otherwise start at the countries
      var startView = 'countries';
      if (this.state.country) {
        var saved = null;
        for (var i = 0; i < this.destinations.countries.length; i++) {
          if (this.destinations.countries[i].iso === this.state.country.iso) saved = this.destinations.countries[i];
        }
        if (saved) {
          this.renderResorts(saved);
          startView = 'resorts';
        }
      }
      this.setWhereView(startView);
      var self = this;
      window.requestAnimationFrame(function () { self.sizeWhereViewport(startView); });
    }
    if (module === 'when') {
      // Re-entering the calendar with "Any dates" set would open the
      // greyed-out Not sure yet view, which reads as a disabled modal
      // (and on the inline PDP, which has no toggle, is a dead end).
      // Start on Set dates so dates are pickable straight away; close()
      // re-selects Any dates if the shopper dismisses without a range
      if (this.state.undecided) {
        this.setUndecided(false);
      }
      this.renderCalendar();
    }
    this.syncUi();
  };

  /**
   * Leaving the not-stocked verdict by ANY route - dismissal, Esc, or
   * hopping to another module - is the same decision as Continue
   * browsing: the picked country does not stock this product, so the PDP
   * is not a place the shopper can stay. Mid-check exits fall through
   * (no verdict yet, nothing to act on). Returns true when navigation
   * was dispatched.
   */
  TripTool.prototype.resolveMarketVerdict = function () {
    var slideContinue = this.querySelector('[data-market-slide-continue]');
    if (
      this.getAttribute('data-open') === 'where'
      && this.getAttribute('data-where-view') === 'notstocked'
      && slideContinue && !slideContinue.hidden
      && this.__marketPick
    ) {
      var pick = this.__marketPick;
      this.__marketPick = null;
      document.dispatchEvent(new CustomEvent('trip:market-continue', { detail: pick }));
      return true;
    }
    return false;
  };

  TripTool.prototype.close = function () {
    this.resolveMarketVerdict();
    var wasWhen = this.getAttribute('data-open') === 'when';
    this.removeAttribute('data-open');
    document.body.classList.remove('trip-tool-lock');
    // Dismissing the calendar mid-flow means "any dates" - auto-select the
    // undecided mode (never on the PDP, where real dates are mandatory)
    if (
      wasWhen
      && !this.classList.contains('trip-tool--inline')
      && !(this.state.start && this.state.end)
      && !this.state.undecided
    ) {
      this.setUndecided(true);
    }
    // Re-sync so the mobile collapsed sheet bars hide with the modules -
    // without this the Where bar lingers on screen after closing from When
    this.syncUi();
    // A queued market switch runs now the flow is over - whether dates were
    // picked or the shopper dismissed without them
    if (this.marketRefresh()) return;
    if (this.returnFocus && this.returnFocus.focus) {
      this.returnFocus.focus();
      this.returnFocus = null;
    }
  };

  TripTool.prototype.setWhereView = function (view) {
    this.setAttribute('data-where-view', view);
    if (view === 'resorts' && this.els.resortSearch) {
      this.els.resortSearch.value = '';
      this.filterResorts('');
    }
    this.sizeWhereViewport(view);
  };

  /**
   * Third where slide: the not-stocked notice. 'checking' shows the pending
   * label while the availability probe runs; a country name shows the
   * verdict with Continue. Back is wired like Change country.
   */
  TripTool.prototype.showMarketPanel = function (state) {
    var panel = this.querySelector('.trip-tool__where-view--notstocked');
    if (!panel) return;
    var text = panel.querySelector('[data-notstocked-text]');
    var hint = panel.querySelector('[data-notstocked-hint]');
    var continueBtn = panel.querySelector('[data-market-slide-continue]');
    if (state === 'checking') {
      text.textContent = panel.dataset.checkingLabel || '';
      if (hint) hint.hidden = true;
      continueBtn.hidden = true;
    } else {
      var template = panel.dataset.notstockedTemplate || "This product isn't stocked in [country]";
      text.textContent = template.replace('[country]', state);
      if (hint) hint.hidden = false;
      continueBtn.hidden = false;
    }
    this.setWhereView('notstocked');
    var self = this;
    window.requestAnimationFrame(function () { self.sizeWhereViewport('notstocked'); });
  };

  /**
   * The where panel keeps its box while content slides, so the viewport is
   * sized to the ACTIVE view - no dead space after Change country.
   */
  TripTool.prototype.sizeWhereViewport = function (view) {
    var viewport = this.querySelector('.trip-tool__where-viewport');
    var known = { resorts: 'resorts', notstocked: 'notstocked' };
    var target = this.querySelector('.trip-tool__where-view--' + (known[view] || 'countries'));
    if (!viewport || !target) return;
    viewport.style.height = target.offsetHeight + 'px';
  };

  /**
   * Where module
   */
  TripTool.prototype.renderCountries = function () {
    if (!this.els.countryList) return;
    var self = this;
    this.els.countryList.textContent = '';

    this.destinations.countries.forEach(function (country) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'trip-tool__row';
      if (self.state.country && self.state.country.iso === country.iso) btn.classList.add('is-selected');

      var flag = document.createElement('span');
      flag.className = 'trip-tool__row-flag';
      var img = document.createElement('img');
      img.src = country.flag || '';
      img.alt = '';
      img.width = 20;
      img.height = 20;
      flag.appendChild(img);

      var label = document.createElement('span');
      label.className = 'trip-tool__row-label';
      label.textContent = country.name;

      var chevron = document.createElement('span');
      chevron.className = 'trip-tool__row-chevron';
      chevron.appendChild(self.cloneTemplate('chevron-circle'));

      btn.appendChild(flag);
      btn.appendChild(label);
      btn.appendChild(chevron);
      btn.addEventListener('click', function () { self.pickCountry(country); });
      // Intent to pick: start (or finish) loading this country's towns now
      var warm = function () { self.fetchTowns(country, 'ski'); self.fetchTowns(country, 'all'); };
      btn.addEventListener('pointerenter', warm);
      btn.addEventListener('focus', warm);
      btn.addEventListener('touchstart', warm, { passive: true });
      li.appendChild(btn);
      self.els.countryList.appendChild(li);
    });
  };

  TripTool.prototype.pickCountry = function (country) {
    this.state.country = { iso: country.iso, name: country.name, market: country.market || '' };
    this.state.resort = null;
    // Warm the availability verdict while the shopper browses resorts, so
    // the not-stocked slide can appear instantly on the resort pick
    if (this.classList.contains('trip-tool--inline')) {
      var current = this.contextIso();
      if (country.market && country.iso && country.iso !== current) {
        document.dispatchEvent(new CustomEvent('trip:market-prefetch', { detail: { country: country } }));
      }
    }
    this.renderResorts(country);
    this.setWhereView('resorts');
    var self = this;
    window.requestAnimationFrame(function () { self.sizeWhereViewport('resorts'); });
    this.syncUi();
  };

  TripTool.prototype.renderResorts = function (country) {
    if (!this.els.resortList) return;
    var self = this;
    var featured = (country.resorts || []).map(function (resort) {
      return townRow(resort.name, resort.region || '', undefined, 1, resort.ski == null ? 1 : resort.ski, 0, []);
    });
    markAmbiguousNames(featured);
    this.resortRows = featured;
    this.resortCountry = country;
    this.resortTier = 0;
    this.setResortsLabel(country);
    this.renderResortRows(this.visibleResortRows(''));

    // Tiers land in order of size: featured (inline) now, then the ski
    // resorts, then every town. Each only replaces a smaller tier for the
    // same country; with the caches warm both land before the slide ends,
    // and a slow proxy costs nothing visible beyond the hint.
    var hint = this.querySelector('[data-resorts-loading]');
    var apply = function (tier, towns) {
      if (!towns || self.resortCountry !== country || TOWNS_TIER_RANK[tier] <= self.resortTier) return;
      self.resortTier = TOWNS_TIER_RANK[tier];
      var seen = {};
      towns.forEach(function (town) { seen[normaliseText(town.name)] = true; });
      // Featured rows the feed does not know (seed mode) stay in front.
      var extra = featured.filter(function (row) { return !seen[normaliseText(row.name)]; });
      self.resortRows = extra.concat(towns);
      markAmbiguousNames(self.resortRows);
      var term = self.els.resortSearch ? self.els.resortSearch.value : '';
      self.renderResortRows(self.visibleResortRows(term));
    };
    if (hint) hint.hidden = false;
    this.fetchTowns(country, 'ski').then(function (towns) { apply('ski', towns); });
    this.fetchTowns(country, 'all').then(function (towns) {
      apply('all', towns);
      if (hint && self.resortCountry === country) hint.hidden = true;
    });
  };

  TripTool.prototype.setResortsLabel = function (country) {
    if (!this.els.resortsLabel) return;
    this.els.resortsLabel.textContent = (this.dataset.labelTownsIn || 'Towns in') + ' ' + country.name;
  };

  /**
   * Which rows to show: with no search, the featured resorts plus the ski
   * resorts (already ranked by the feed) up to a screenful; with a search,
   * every match, name-and-region, accent-insensitive, capped so 15,000
   * French towns never hit the DOM at once.
   */
  var RESORT_ROWS_IDLE = 40;
  var RESORT_ROWS_SEARCH = 60;

  TripTool.prototype.visibleResortRows = function (term) {
    var rows = this.resortRows || [];
    var needle = normaliseText(term).trim();
    if (!needle) {
      var idle = rows.filter(function (row) { return row.featured || row.ski; });
      return idle.slice(0, RESORT_ROWS_IDLE);
    }
    // Scored search: every typed word has to land somewhere - a word of
    // the name, of an alternate name, or of the region, in any order - and
    // the rows are ranked by how well (see scoreRow). Feed order (featured,
    // ski resorts by size, population) breaks ties, so "bern" gives Bern
    // before every village in canton Bern. Candidates are capped so a
    // single letter never sorts all 15,000 French towns; feed order makes
    // the first few thousand the ones worth ranking anyway.
    var tokens = needle.split(' ');
    var squashed = squashText(needle);
    var candidates = [];
    for (var i = 0; i < rows.length && candidates.length < SEARCH_CANDIDATES; i++) {
      var score = scoreRow(rows[i], tokens, squashed);
      if (score > 0) candidates.push({ row: rows[i], score: score, order: i });
    }
    candidates.sort(function (a, b) { return b.score - a.score || a.order - b.order; });
    return candidates.slice(0, RESORT_ROWS_SEARCH).map(function (candidate) { return candidate.row; });
  };

  /**
   * Ranking weights. A typed word matching a name word exactly beats a
   * prefix beats a substring; the name beats an alternate name beats the
   * region. Bonuses for typed words landing adjacent and in order and for
   * covering the first word, a small penalty per name word left uncovered
   * (so "St. Moritz" beats "St. Moritz-Bad" for "st moritz", while "moritz"
   * alone still puts the exact word above "Moritzberg"). Bonuses and the
   * penalty only apply to clean name matches: a substring, alias or region
   * hit keeps its flat score, so those weaker results sit in feed order.
   */
  var SCORE = {
    exact: 100, prefix: 70, contains: 30,
    aliasExact: 80, aliasPrefix: 55, aliasContains: 25,
    region: 20, regionPrefix: 15,
    adjacent: 15, leading: 10, uncovered: 5, maxUncovered: 15
  };
  var SEARCH_CANDIDATES = 3000;

  /** Best way one typed word matches a list of words: exact, then prefix, then substring (three letters or more - "st" inside "orsta" is noise). */
  function matchWord(token, words, exact, prefix, contains) {
    var best = null;
    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      var score = 0;
      var quality = 0;
      if (word === token) { score = exact; quality = 3; }
      else if (word.indexOf(token) === 0) { score = prefix; quality = 2; }
      else if (contains && token.length >= 3 && word.indexOf(token) !== -1) { score = contains; quality = 1; }
      if (quality && (!best || quality > best.quality)) best = { score: score, quality: quality, index: i };
      if (quality === 3) break;
    }
    return best;
  }

  function matchAliasWord(row, token) {
    var best = null;
    for (var i = 0; i < row.aliasWords.length; i++) {
      var found = matchWord(token, row.aliasWords[i], SCORE.aliasExact, SCORE.aliasPrefix, SCORE.aliasContains);
      if (found && (!best || found.score > best.score)) best = found;
    }
    return best;
  }

  function scoreRow(row, tokens, squashed) {
    var words = row.words;
    if (!words.length) return 0;
    // The whole query against the name with spaces removed: "saasfee",
    // "stmoritz" - as good as typing it with the spaces
    if (row.squashed === squashed) return SCORE.exact * words.length + SCORE.leading + SCORE.adjacent + 1;
    var total = 0;
    var clean = true;
    var missing = false;
    var covered = {};
    var coveredCount = 0;
    var lastIndex = -1;
    var ordered = true;
    for (var t = 0; t < tokens.length; t++) {
      var token = tokens[t];
      var found = matchWord(token, words, SCORE.exact, SCORE.prefix, SCORE.contains);
      if (found && found.quality >= 2) {
        if (!covered[found.index]) { covered[found.index] = true; coveredCount++; }
        if (lastIndex !== -1 && found.index !== lastIndex + 1) ordered = false;
        lastIndex = found.index;
      } else {
        clean = false;
        if (!found) found = matchAliasWord(row, token);
        if (!found) found = matchWord(token, row.regionWords, SCORE.region, SCORE.regionPrefix, 0);
        if (!found) { missing = true; continue; }
      }
      total += found.score;
    }
    if (missing) {
      // A typed word matched nothing word-wise; only the spaceless forms
      // can still rescue it ("saasf" against "saasfee")
      total = 0;
      if (row.squashed.indexOf(squashed) === 0) {
        total = SCORE.prefix + SCORE.leading - Math.min(SCORE.maxUncovered, SCORE.uncovered * (words.length - 1));
      }
      for (var a = 0; a < row.aliasesSquashed.length; a++) {
        if (row.aliasesSquashed[a] === squashed) total = Math.max(total, SCORE.aliasExact * tokens.length);
        else if (row.aliasesSquashed[a].indexOf(squashed) === 0) total = Math.max(total, SCORE.aliasPrefix);
      }
      return total;
    }
    if (clean) {
      if (tokens.length > 1 && ordered) total += SCORE.adjacent;
      if (covered[0]) total += SCORE.leading;
      total -= Math.min(SCORE.maxUncovered, SCORE.uncovered * (words.length - coveredCount));
    }
    return total;
  }

  TripTool.prototype.renderResortRows = function (rows) {
    var self = this;
    var list = this.els.resortList;
    list.textContent = '';
    rows.forEach(function (row) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'trip-tool__row' + (row.ski ? ' trip-tool__row--ski' : ' trip-tool__row--town') + (row.featured ? ' trip-tool__row--featured' : '');
      btn.setAttribute('data-resort-row', '');
      if (self.state.resort && self.state.resort.name === row.name) btn.classList.add('is-selected');

      // Ski resorts get the skier, plain towns the map pin, both in the pin colour
      var icon = document.createElement('span');
      icon.className = 'trip-tool__row-icon';
      icon.appendChild(self.cloneTemplate(row.ski ? 'ski' : 'location'));
      icon.style.color = 'var(--cirkel-color-3, #30757a)';

      var label = document.createElement('span');
      label.className = 'trip-tool__row-label';
      // The region only appears when it is needed to tell two towns of the
      // same name apart: "Saint-Martin (Valais)" next to "Saint-Martin (Fribourg)"
      label.textContent = row.ambiguous && row.region ? row.name + ' (' + row.region + ')' : row.name;

      btn.appendChild(icon);
      btn.appendChild(label);
      btn.addEventListener('click', function () { self.pickResort(row); });
      li.appendChild(btn);
      list.appendChild(li);
    });
    var emptyMsg = this.querySelector('[data-no-resorts]');
    if (emptyMsg) emptyMsg.hidden = rows.length > 0;
    if (this.getAttribute('data-where-view') === 'resorts') {
      window.requestAnimationFrame(function () { self.sizeWhereViewport('resorts'); });
    }
  };

  TripTool.prototype.filterResorts = function (term) {
    if (!this.els.resortList) return;
    this.renderResortRows(this.visibleResortRows(term));
  };

  TripTool.prototype.pickResort = function (resort) {
    this.state.resort = { name: resort.name, region: resort.region || resort.town || '', town: resort.region || resort.town || '' };
    TripState.write(this.state);
    // Inline (PDP): a destination in another market hands over to the
    // market flow AFTER the resort pick (per review) - trip-pdp.js checks
    // stock in the target market and either switches or shows the modal
    if (this.classList.contains('trip-tool--inline')) {
      var currentCountry = this.contextIso();
      var c = this.state.country;
      if (c && c.market && c.iso && c.iso !== currentCountry) {
        this.__marketPick = { country: c, resort: this.state.resort, previous: this.__preWhereState || null };
        this.showMarketPanel('checking');
        document.dispatchEvent(new CustomEvent('trip:market-probe', { detail: this.__marketPick }));
        return;
      }
    }
    // Collections: a destination in another market queues a refresh for
    // when the flow ends (dates picked or dismissed) instead of yanking
    // the page away mid-flow
    if (this.dataset.pageType === 'collection') {
      var pendingIso = this.state.country && this.state.country.iso;
      var mapped = this.state.country && this.state.country.market;
      this.__pendingMarketSwitch = (mapped && pendingIso && pendingIso !== this.contextIso()) ? pendingIso : null;
    } else if (!this.classList.contains('trip-tool--inline')) {
      this.deferMarketSwitch();
    }
    // When already answered (dates set, or "any dates" anywhere off the
    // PDP), a location change is just a location change - close and apply
    // instead of reopening the calendar
    var whenAnswered = (this.state.start && this.state.end) ||
      (this.state.undecided && !this.classList.contains('trip-tool--inline'));
    if (whenAnswered) {
      this.close();
      return;
    }
    // Airbnb-style flow: destination chosen, advance to dates
    this.open('when');
  };

  /**
   * On collection pages a destination change switches the Shopify market and
   * reloads results (design decision: the only page that reloads - a reload
   * on the home page would be jarring; elsewhere the switch waits for the
   * shopper's next navigation, see deferMarketSwitch and CIRKEL-273).
   * The switch queues at resort-pick time and runs here when the flow ends.
   * Returns true when a market switch navigation was started.
   */
  TripTool.prototype.marketRefresh = function () {
    if (this.dataset.pageType !== 'collection') return false;
    var iso = this.__pendingMarketSwitch;
    this.__pendingMarketSwitch = null;
    if (!iso || iso === this.contextIso()) return false;
    this.navigateWithCountry(window.location.pathname + window.location.search, iso);
    return true;
  };

  /**
   * Every other page with the floating tool: a destination in another
   * market leaves this page as rendered and saves the switch for the next
   * navigation (PendingMarket). A destination back in the rendered market,
   * one with no market of its own, or none at all calls it off.
   */
  TripTool.prototype.deferMarketSwitch = function () {
    var country = this.state.country;
    var iso = country && country.market && country.iso ? country.iso.toLowerCase() : null;
    var rendered = this.contextIso();
    if (iso && iso !== rendered) {
      PendingMarket.queue(iso);
    } else if (PendingMarket.read()) {
      PendingMarket.clear(rendered);
    }
  };

  /**
   * Rolls the trip back to a snapshot (market flow: "Back to countries"
   * un-does a mis-picked destination without switching anything).
   */
  TripTool.prototype.restoreState = function (snapshot) {
    if (!snapshot) return;
    this.state = JSON.parse(JSON.stringify(snapshot));
    TripState.write(this.state);
    this.renderCountries();
    this.syncUi();
  };

  TripTool.prototype.pickBrowsing = function () {
    this.state.country = null;
    this.state.resort = null;
    TripState.write(this.state);
    if (this.dataset.pageType !== 'collection' && !this.classList.contains('trip-tool--inline')) {
      this.deferMarketSwitch();
    }
    this.open('when');
  };

  /**
   * When module (calendar)
   */
  TripTool.prototype.shiftMonth = function (delta) {
    this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + delta, 1);
    this.renderCalendar();
  };

  TripTool.prototype.renderCalendar = function () {
    if (!this.els.months) return;
    var self = this;
    this.els.months.querySelectorAll('.trip-tool__month').forEach(function (el) { el.remove(); });

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var count = isMobile ? 1 : 2;
    for (var i = 0; i < count; i++) {
      var month = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + i, 1);
      this.els.months.appendChild(this.buildMonth(month, i === 1));
    }

    var prev = this.querySelector('[data-month-prev]');
    if (prev) {
      var now = new Date();
      prev.disabled = this.viewMonth <= new Date(now.getFullYear(), now.getMonth(), 1);
    }
    this.syncNotice();
  };

  /**
   * Calendar notice: the 2-14 day rule by default; the last-minute warning
   * (a link to the last-minute page) whenever the chosen or pending start
   * sits inside the lead-time buffer. Writes are guarded so re-renders
   * stay no-ops when nothing changed.
   */
  TripTool.prototype.syncNotice = function () {
    var notice = this.querySelector('[data-notice]');
    var warning = this.querySelector('[data-notice-warning]');
    if (!notice || !warning) return;
    var start = this.pendingStart || this.state.start;
    var short = !this.state.undecided && isShortNotice(start);
    if (notice.hidden !== short) notice.hidden = short;
    if (warning.hidden !== !short) warning.hidden = !short;
    this.classList.toggle('trip-tool--short-notice', short);
  };

  TripTool.prototype.buildMonth = function (monthDate, isSecond) {
    var self = this;
    var wrap = document.createElement('div');
    wrap.className = 'trip-tool__month' + (isSecond ? ' trip-tool__month--second' : '');

    var showYear = monthDate.getFullYear() !== new Date().getFullYear() || window.matchMedia('(max-width: 749px)').matches;
    var title = document.createElement('div');
    title.className = 'trip-tool__month-title';
    title.textContent = MONTHS[monthDate.getMonth()] + (showYear ? ' ' + monthDate.getFullYear() : '');
    wrap.appendChild(title);

    var weekdays = document.createElement('div');
    weekdays.className = 'trip-tool__weekdays';
    WEEKDAYS.forEach(function (d) {
      var cell = document.createElement('div');
      cell.className = 'trip-tool__weekday';
      cell.textContent = d;
      weekdays.appendChild(cell);
    });
    wrap.appendChild(weekdays);

    var grid = document.createElement('div');
    grid.className = 'trip-tool__grid';

    var firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    var lead = (firstDay.getDay() + 6) % 7; // Monday-first
    var daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
    var today = todayIso();

    var monthFirstIso = iso(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
    var monthLastIso = iso(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));
    var rangeStart = this.pendingStart || this.state.start;
    var rangeEnd = this.pendingStart ? null : this.state.end;
    var continuesInFromPrev = Boolean(rangeStart && rangeEnd && rangeStart < monthFirstIso && rangeEnd >= monthFirstIso);
    var continuesOutToNext = Boolean(rangeStart && rangeEnd && rangeEnd > monthLastIso && rangeStart <= monthLastIso);

    for (var b = 0; b < lead; b++) {
      var blank = document.createElement('span');
      blank.className = 'trip-tool__day trip-tool__day--blank';
      // The strip fades in from the previous month across the last lead blank
      if (continuesInFromPrev && b === lead - 1) blank.classList.add('is-range-tail-left');
      grid.appendChild(blank);
    }

    for (var day = 1; day <= daysInMonth; day++) {
      var dateIso = iso(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'trip-tool__day';
      cell.setAttribute('data-date', dateIso);
      var num = document.createElement('span');
      num.textContent = String(day);
      cell.appendChild(num);

      cell.disabled = this.isDayDisabled(dateIso, today);
      this.applyRangeClasses(cell, dateIso);

      cell.addEventListener('click', (function (d) {
        return function () { self.pickDay(d); };
      })(dateIso));

      if (continuesInFromPrev && dateIso === monthFirstIso) cell.classList.add('is-cont-left');
      if (continuesOutToNext && dateIso === monthLastIso) cell.classList.add('is-cont-right');

      grid.appendChild(cell);
    }

    // Trailing blanks complete the final row; the strip fades out across the
    // first one when the range continues into the next month
    var totalCells = lead + daysInMonth;
    var trail = (7 - (totalCells % 7)) % 7;
    for (var tb = 0; tb < trail; tb++) {
      var tblank = document.createElement('span');
      tblank.className = 'trip-tool__day trip-tool__day--blank';
      if (continuesOutToNext && tb === 0) tblank.classList.add('is-range-tail-right');
      grid.appendChild(tblank);
    }

    wrap.appendChild(grid);
    return wrap;
  };

  TripTool.prototype.isDayDisabled = function (dateIso, today) {
    if (dateIso < today) return true;
    // Anything from tomorrow is selectable; a start inside the lead-time
    // buffer gets the short-notice warning rather than a disabled cell
    if (dateIso < offsetIso(today, MIN_START_OFFSET)) return true;
    // Mid-selection: hard 2-14 day rule (inclusive day count, matching legacy)
    if (this.pendingStart) {
      var diff = daysBetween(this.pendingStart, dateIso);
      if (diff !== 0 && (diff < MIN_DAYS - 1 || diff > MAX_DAYS - 1)) return true;
      if (diff < 0) return true;
    }
    return false;
  };

  TripTool.prototype.applyRangeClasses = function (cell, dateIso) {
    var start = this.pendingStart || this.state.start;
    var end = this.pendingStart ? null : this.state.end;
    if (!start) return;
    // A lone start date is a circle only; the strip needs both ends
    if (dateIso === start) {
      cell.classList.add('is-start');
      if (end) cell.classList.add('is-range');
    }
    if (end) {
      if (dateIso === end) cell.classList.add('is-end', 'is-range');
      if (dateIso > start && dateIso < end) cell.classList.add('is-range');
    }
  };

  TripTool.prototype.pickDay = function (dateIso) {
    if (!this.pendingStart) {
      this.pendingStart = dateIso;
      this.state.start = dateIso;
      this.state.end = null;
    } else {
      if (dateIso === this.pendingStart) {
        this.pendingStart = null;
        this.state.start = null;
        this.renderCalendar();
        this.syncUi();
        return;
      }
      this.state.start = this.pendingStart;
      this.state.end = dateIso;
      this.state.undecided = false;
      this.pendingStart = null;
      TripState.write(this.state);
      // The trip is complete once the range lands - show the selected end
      // for a beat, then dismiss and let close() apply whatever is queued
      // (on collections that includes a pending market refresh). A
      // short-notice start keeps the calendar open so the warning callout
      // stays readable; the shopper dismisses it themselves
      var self = this;
      this.renderCalendar();
      this.syncUi();
      if (!isShortNotice(this.state.start)) {
        window.setTimeout(function () { self.close(); }, 350);
      }
      return;
    }
    this.renderCalendar();
    this.syncUi();
  };

  TripTool.prototype.clearDates = function () {
    this.pendingStart = null;
    this.state.start = null;
    this.state.end = null;
    TripState.write(this.state);
    this.renderCalendar();
    this.syncUi();
  };

  TripTool.prototype.setUndecided = function (value) {
    this.state.undecided = value;
    if (value) {
      this.pendingStart = null;
      this.state.start = null;
      this.state.end = null;
    }
    TripState.write(this.state);
    this.classList.toggle('trip-tool--undecided', value);
    this.renderCalendar();
    this.syncUi();
  };

  /**
   * Search action
   * Navigates to the all-products collection; switches Shopify market first
   * when the chosen country maps to one.
   */
  /**
   * The active buyer-context country. The server-rendered attribute is the
   * truth; window.Shopify.country is a fallback and localhost renders it
   * empty, so the final default assumes the primary market
   */
  /**
   * The ?country param has done its job once the page renders - scrub it
   * from the address bar so later navigation and sharing cannot re-force a
   * stale context or stack duplicate params. Returns true when the page was
   * requested with one.
   */
  TripTool.prototype.scrubCountryParam = function () {
    if (window.location.search.indexOf('country=') === -1) return false;
    var parsed = new URL(window.location.href);
    var named = parsed.searchParams.has('country');
    parsed.searchParams.delete('country');
    window.history.replaceState(window.history.state, '', parsed.pathname + parsed.search + parsed.hash);
    return named;
  };

  TripTool.prototype.contextIso = function () {
    return (this.dataset.contextIso || (window.Shopify && window.Shopify.country) || 'ch').toLowerCase();
  };

  /**
   * Context switch by navigation: the ?country param persists the buyer
   * context on a plain GET, so no /localization POST is needed - which the
   * localhost dev proxy cannot handle (502)
   */
  TripTool.prototype.navigateWithCountry = function (url, iso) {
    var parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('country', iso.toUpperCase());
    try { window.localStorage.setItem('cs-context-iso', iso.toUpperCase()); } catch (error) { /* private mode */ }
    window.location.assign(parsed.pathname + parsed.search);
  };

  /**
   * Context enforcement: a history/back navigation can restore a page that
   * was RENDERED in a country the session has since left (Swiss content on
   * a France session). Every switch records the intended country; a page
   * whose rendered country disagrees reloads once so the shopper only ever
   * sees the market they selected. The one-shot flag stops a loop when the
   * context was changed by something outside this code - the fresh render
   * then becomes the accepted truth.
   *
   * A deferred switch (PendingMarket) that no link carried is applied here,
   * once, by re-requesting this page with ?country. An address that named a
   * country itself settles it (the link carried it, or the URL chose one),
   * as does a render already in that country or the one attempt made - and
   * the render it settles on becomes the intended country, no extra reload.
   */
  TripTool.prototype.enforceContext = function (arrivedWithCountry) {
    var rendered = (this.dataset.contextIso || '').toUpperCase();
    if (!rendered) return;
    var stored = null;
    try { stored = window.localStorage.getItem('cs-context-iso'); } catch (error) { return; }
    var reloaded = null;
    try { reloaded = window.sessionStorage.getItem('cs-context-reloaded'); } catch (error) { /* private mode */ }
    var pending = PendingMarket.read();
    if (pending) {
      if (!arrivedWithCountry && !reloaded && pending !== rendered) {
        try { window.sessionStorage.setItem('cs-context-reloaded', '1'); } catch (error) { /* private mode */ }
        var here = new URL(window.location.href);
        here.searchParams.set('country', pending);
        window.location.replace(here.pathname + here.search + here.hash);
        return;
      }
      PendingMarket.clear(rendered);
      stored = rendered;
    }
    if (!stored || reloaded) {
      try {
        window.localStorage.setItem('cs-context-iso', rendered);
        window.sessionStorage.removeItem('cs-context-reloaded');
      } catch (error) { /* private mode */ }
      return;
    }
    if (stored !== rendered) {
      try { window.sessionStorage.setItem('cs-context-reloaded', '1'); } catch (error) { /* private mode */ }
      window.location.reload();
    }
  };

  TripTool.prototype.search = function () {
    var hasWhere = Boolean(this.state.country);
    var hasWhen = Boolean((this.state.start && this.state.end) || this.state.undecided);
    // Dates are optional at search time: searching without a range simply
    // means "any dates" - select it and carry on rather than blocking
    if (!hasWhen) {
      this.setUndecided(true);
      hasWhen = true;
    }
    // No destination but the when step is complete (dates or an explicit
    // "not sure yet" / "just browsing") - browse the home market. The pick
    // is a browsing context, not a destination: the pill keeps asking
    // "Where are you going?" rather than reading Switzerland (CIRKEL-272)
    var country = this.state.country;
    if (!hasWhere && hasWhen) {
      for (var d = 0; d < this.destinations.countries.length; d++) {
        if (this.destinations.countries[d].iso === 'ch') {
          country = this.destinations.countries[d];
          hasWhere = true;
          break;
        }
      }
    }
    if (!hasWhere || !hasWhen) {
      this.showSearchError(hasWhere ? 'when' : 'where');
      return;
    }
    TripState.write(this.state);
    var target = this.dataset.collectionUrl || '/collections/all';
    var market = country && country.market;
    var currentCountry = this.contextIso();

    if (market && country.iso !== currentCountry) {
      this.navigateWithCountry(target, country.iso);
      return;
    }
    window.location.assign(target);
  };

  /**
   * Incomplete-search error: red bubble above the pill naming the missing
   * half, red ring on that half. Cleared on open or after a short hold.
   */
  TripTool.prototype.showSearchError = function (missing) {
    var bubble = this.querySelector('[data-trip-error]');
    if (!bubble) return;
    var msgKeys = { where: 'msgWhere', when: 'msgWhen', locked: 'msgLocked' };
    bubble.textContent = bubble.dataset[msgKeys[missing] || 'msgWhere'];
    bubble.hidden = false;
    this.classList.add('trip-tool--error');
    this.classList.toggle('trip-tool--error-where', missing === 'where');
    this.classList.toggle('trip-tool--error-when', missing === 'when');
    window.clearTimeout(this.__errorTimer);
    var self = this;
    this.__errorTimer = window.setTimeout(function () { self.clearSearchError(); }, 4000);
  };

  TripTool.prototype.clearSearchError = function () {
    window.clearTimeout(this.__errorTimer);
    var bubble = this.querySelector('[data-trip-error]');
    if (bubble) bubble.hidden = true;
    this.classList.remove('trip-tool--error', 'trip-tool--error-where', 'trip-tool--error-when');
  };

  /**
   * UI sync: pill labels, sheet bars, toggle states
   */
  TripTool.prototype.syncUi = function () {
    var whereText = null;
    if (this.state.resort && this.state.country) {
      whereText = this.state.resort.name + ', ' + this.state.country.name;
    } else if (this.state.country) {
      whereText = this.state.country.name;
    }

    var whenText = null;
    if (this.state.start && this.state.end) {
      whenText = formatShort(this.state.start) + ' - ' + formatShort(this.state.end);
    } else if (this.state.start) {
      whenText = formatShort(this.state.start) + ' -';
    } else if (this.state.undecided) {
      whenText = this.dataset.labelAnyDates || 'Any dates';
    }

    if (this.els.whereLabel) this.els.whereLabel.textContent = whereText || this.els.whereLabel.dataset.placeholder;
    if (this.els.whenLabel) this.els.whenLabel.textContent = whenText || this.els.whenLabel.dataset.placeholder;

    var filled = Boolean(whereText || whenText);
    this.classList.toggle('trip-tool--filled', filled);

    if (this.els.compactPrimary) {
      this.els.compactPrimary.textContent = filled
        ? (whereText || this.els.whereLabel.dataset.placeholder)
        : this.els.compactPrimary.dataset.placeholder;
    }
    if (this.els.compactSecondary) {
      this.els.compactSecondary.textContent = whenText || this.els.compactSecondary.dataset.placeholder;
    }

    this.syncNotice();

    if (this.els.sheetBarWhereValue) this.els.sheetBarWhereValue.textContent = whereText || '';
    if (this.els.sheetBarWhenValue) this.els.sheetBarWhenValue.textContent = whenText || this.els.sheetBarWhenValue.dataset.placeholder;

    var open = this.getAttribute('data-open');
    if (this.els.sheetBarWhere) this.els.sheetBarWhere.hidden = open !== 'when';
    if (this.els.sheetBarWhen) this.els.sheetBarWhen.hidden = open !== 'where';

    var dates = this.querySelector('[data-toggle-dates]');
    var unsure = this.querySelector('[data-toggle-unsure]');
    if (dates) dates.setAttribute('aria-pressed', String(!this.state.undecided));
    if (unsure) unsure.setAttribute('aria-pressed', String(this.state.undecided));
    // Boolean() guard: toggle with undefined flips instead of removing
    this.classList.toggle('trip-tool--undecided', Boolean(this.state.undecided));
  };

  // Not-sure buttons (both views) share one handler
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-not-sure]');
    if (!btn) return;
    var tool = btn.closest('trip-tool');
    if (tool && tool.pickBrowsing) tool.pickBrowsing();
  });

  /**
   * While a market switch is pending (PendingMarket), the link the shopper
   * follows gets ?country the moment it is followed - plain and new-tab
   * clicks, and the context menu's open in new tab - so the next page
   * renders in the new market on its first load. Runs last (window, bubble
   * phase): a link a script handles itself (ajax facets) has its default
   * prevented and is left alone, and a script-driven navigation is caught
   * on landing instead. The original href is back on the next tick, so a
   * page restored from the back/forward cache never replays a stale country.
   */
  function bareHost(host) {
    return host.replace(/^www\./, '');
  }

  function decorateLink(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    // Shopify's own country selector (mobile menu) chooses the market
    // outright - it overrules a switch still waiting for a navigation
    var countryPick = event.type === 'click' && target.closest('localization-form a[data-value]');
    var pickForm = countryPick && countryPick.closest('form');
    if (pickForm && pickForm.querySelector('input[name="country_code"]')) {
      PendingMarket.clear(countryPick.dataset.value);
      return;
    }
    if (event.defaultPrevented) return;
    var pending = PendingMarket.read();
    if (!pending) return;
    var link = target.closest('a[href]');
    if (!link || link.hasAttribute('download')) return;
    var href = link.getAttribute('href');
    var url;
    try {
      url = new URL(href, document.baseURI);
    } catch (error) {
      return;
    }
    // This store only, with or without www - never mailto:, tel: or other sites
    if (!/^https?:$/.test(url.protocol) || bareHost(url.host) !== bareHost(window.location.host)) return;
    // In-page anchors (bare "#" too) keep scrolling; a country the link names itself stands
    var samePage = url.pathname === window.location.pathname && url.search === window.location.search;
    if ((samePage && href.indexOf('#') !== -1) || url.searchParams.has('country')) return;
    url.searchParams.set('country', pending);
    link.setAttribute('href', url.href);
    window.setTimeout(function () { link.setAttribute('href', href); }, 0);
  }

  window.addEventListener('click', decorateLink);
  window.addEventListener('auxclick', decorateLink);
  window.addEventListener('contextmenu', decorateLink);

  customElements.define('trip-tool', TripTool);
})();

